/**
 * Copy Ask Crate Hackers may show. No vendor names, env vars, or stack traces.
 */

export const CHAT_UNAVAILABLE =
  'Ask Crate Hackers is not available right now. Please try again later.';

export const CHAT_GENERIC_FAILURE =
  'Ask Crate Hackers hit a problem. Please try again.';

export const CHAT_TIMEOUT =
  'Ask Crate Hackers timed out. Try a smaller question.';

export const CHAT_BUSY =
  'Ask Crate Hackers is busy. Please try again in a moment.';

export const CHAT_OFFLINE =
  'Ask Crate Hackers could not connect. Check your internet and try again.';

export const CHAT_LENGTH_LIMIT =
  'The reply hit the length limit. Try a narrower question.';

export const CHAT_TOO_MANY_TOOLS =
  'The assistant used too many tool steps. Try asking again.';

export const CHAT_STOPPED_EARLY =
  'The assistant stopped before it could finish. Try asking again.';

export const CHAT_CANCELLED = 'The run was cancelled.';

const PRODUCT_ERRORS = new Set([
  CHAT_UNAVAILABLE,
  CHAT_GENERIC_FAILURE,
  CHAT_TIMEOUT,
  CHAT_BUSY,
  CHAT_OFFLINE,
  CHAT_LENGTH_LIMIT,
  CHAT_TOO_MANY_TOOLS,
  CHAT_STOPPED_EARLY,
  CHAT_CANCELLED,
]);

const asText = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value instanceof Error) {
    // Node's fetch reports "fetch failed" and keeps ENOTFOUND etc. on cause.
    const cause = (value as { cause?: unknown }).cause;
    const causeText = cause instanceof Error ? cause.message : '';
    return `${value.message} ${causeText}`.trim();
  }
  if (value == null) return '';
  return String(value).trim();
};

/**
 * Product copy for an HTTP failure from the chat vendor. 5xx and 529 are the
 * vendor being down or overloaded, which reads as busy rather than broken.
 */
export const chatErrorForHttpStatus = (status: number, detail = ''): string => {
  if (status === 401 || status === 403) return CHAT_UNAVAILABLE;
  if (status === 429 || status >= 500) return CHAT_BUSY;
  if (status === 400 && /credit|spend|billing/i.test(detail)) {
    return CHAT_UNAVAILABLE;
  }
  return CHAT_GENERIC_FAILURE;
};

/** Maps any chat failure to product copy. Unknown or vendor text never passes through. */
export const toUserFacingChatError = (value: unknown): string => {
  const text = asText(value)
    .replace(/^run error:\s*/i, '')
    .trim();
  if (!text) return CHAT_GENERIC_FAILURE;
  if (PRODUCT_ERRORS.has(text)) return text;

  if (
    /not set|no api key|missing.*key|unauthorized|401|invalid.*key|rejected the api/i.test(
      text,
    )
  ) {
    return CHAT_UNAVAILABLE;
  }
  if (/429|rate limit|overloaded|too many requests/i.test(text)) {
    return CHAT_BUSY;
  }
  if (/timed out|timeout|etimedout/i.test(text)) return CHAT_TIMEOUT;
  if (
    /failed to fetch|fetch failed|enotfound|econnreset|econnrefused|network|offline/i.test(
      text,
    )
  ) {
    return CHAT_OFFLINE;
  }
  return CHAT_GENERIC_FAILURE;
};
